import { createAction } from 'redux-api-middleware';
import get from 'lodash/get';
import { buildQueryString, handleResponse, handleError } from '../utils';
import { productSchema } from './products';
import { agreementSchema, contractSchema } from './schemas';

export const CREATE_AGREEMENT_SUCCESS = 'CREATE_AGREEMENT_SUCCESS';
export const CREATE_AGREEMENT_REQUEST = 'CREATE_AGREEMENT_REQUEST';
export const CREATE_AGREEMENT_FAILURE = 'CREATE_AGREEMENT_FAILURE';
export const EDIT_AGREEMENT_SUCCESS = 'EDIT_AGREEMENT_SUCCESS';
export const EDIT_AGREEMENT_REQUEST = 'EDIT_AGREEMENT_REQUEST';
export const EDIT_AGREEMENT_FAILURE = 'EDIT_AGREEMENT_FAILURE';
export const FETCH_AGREEMENT_REQUEST = 'FETCH_AGREEMENT_REQUEST';
export const FETCH_AGREEMENT_SUCCESS = 'FETCH_AGREEMENT_SUCCESS';
export const FETCH_AGREEMENT_FAILURE = 'FETCH_AGREEMENT_FAILURE';
export const FETCH_AGREEMENTS_REQUEST = 'FETCH_AGREEMENTS_REQUEST';
export const FETCH_AGREEMENTS_SUCCESS = 'FETCH_AGREEMENTS_SUCCESS';
export const FETCH_AGREEMENTS_FAILURE = 'FETCH_AGREEMENTS_FAILURE';
export const FETCH_AGREEMENT_PRODUCTS_REQUEST = 'FETCH_AGREEMENT_PRODUCTS_REQUEST';
export const FETCH_AGREEMENT_PRODUCTS_SUCCESS = 'FETCH_AGREEMENT_PRODUCTS_SUCCESS';
export const FETCH_AGREEMENT_PRODUCTS_FAILURE = 'FETCH_AGREEMENT_PRODUCTS_FAILURE';
export const UPDATE_AGREEMENT_PRODUCTS_PAGE = 'UPDATE_AGREEMENT_PRODUCTS_PAGE';
export const SUBMIT_AGREEMENT_FOR_APPROVAL_REQUEST = 'SUBMIT_AGREEMENT_FOR_APPROVAL_REQUEST';
export const SUBMIT_AGREEMENT_FOR_APPROVAL_SUCCESS = 'SUBMIT_AGREEMENT_FOR_APPROVAL_SUCCESS';
export const SUBMIT_AGREEMENT_FOR_APPROVAL_FAILURE = 'SUBMIT_AGREEMENT_FOR_APPROVAL_FAILURE';
export const APPROVE_AGREEMENT_REQUEST = 'APPROVE_AGREEMENT_REQUEST';
export const APPROVE_AGREEMENT_SUCCESS = 'APPROVE_AGREEMENT_SUCCESS';
export const APPROVE_AGREEMENT_FAILURE = 'APPROVE_AGREEMENT_FAILURE';
export const REJECT_AGREEMENT_REQUEST = 'REJECT_AGREEMENT_REQUEST';
export const REJECT_AGREEMENT_SUCCESS = 'REJECT_AGREEMENT_SUCCESS';
export const REJECT_AGREEMENT_FAILURE = 'REJECT_AGREEMENT_FAILURE';
export const AGREEMENT_PRODUCT_PAGE_SIZE = 500;

/**
 * Fetch single agreement related to a specific partner contract.
 *
 * @param {string} contractAddress - Contract address.
 * @param {string} address - Agreement address.
 * @param {object} [options] - Options for fetching.
 * @param {boolean} [options.useCache=true] - If `true`, cache won't be checked & the request will be made.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchAgreement = (contractAddress, address, options = { useCache: true }) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/agreements/${address}`,
    method: 'GET',
    bailout: store => options.useCache && get(store, `entities.agreements.${address}`, false),
    types: [
      FETCH_AGREEMENT_SUCCESS,
      {
        type: FETCH_AGREEMENT_SUCCESS,
        payload: (action, state, res) => handleResponse(agreementSchema, action, state, res),
      },
      { type: FETCH_AGREEMENT_FAILURE, payload: handleError },
    ],
  });

/**
 * Fetch agreements related to a specific partner contract.
 *
 * @param {string} contractAddress - Contract address.
 * @param {object} filters - Query filters for the request.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchAgreements = (contractAddress, filters = {}) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/agreements?${buildQueryString(
      filters,
    )}`,
    method: 'GET',
    types: [
      { type: FETCH_AGREEMENTS_REQUEST },
      {
        type: FETCH_AGREEMENTS_SUCCESS,
        payload: (action, state, res) => handleResponse([agreementSchema], action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      {
        type: FETCH_AGREEMENTS_FAILURE,
        payload: handleError,
        meta: { contractAddress },
      },
    ],
  });

/**
 * Fetch products related to a specific contract agreement.
 *
 * @param {string} contractAddress - Contract address.
 * @param {string} address - Agreement address.
 * @param {object} parameters - Query parameters.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchProducts = (
  contractAddress,
  agreementAddress,
  q,
  { start = 0, count = AGREEMENT_PRODUCT_PAGE_SIZE } = {},
) => {
  const queryString = { start, count };
  queryString.q = !q ? '' : q;
  return createAction({
    endpoint: `${
      window.config.apiUrl
    }/contracts/${contractAddress}/agreements/${agreementAddress}/products?${buildQueryString(
      queryString,
    )}`,
    method: 'GET',
    types: [
      { type: FETCH_AGREEMENT_PRODUCTS_REQUEST, payload: { contractAddress } },
      {
        type: FETCH_AGREEMENT_PRODUCTS_SUCCESS,
        payload: (action, state, res) => handleResponse([productSchema], action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      {
        type: FETCH_AGREEMENT_PRODUCTS_FAILURE,
        payload: handleError,
        meta: { contractAddress },
      },
    ],
  });
};

export const updateAgreementProductsPage =
  (contractAddress, agreementAddress, page) => async dispatch => {
    dispatch({ type: UPDATE_AGREEMENT_PRODUCTS_PAGE, payload: page });

    const start = (page - 1) * AGREEMENT_PRODUCT_PAGE_SIZE;
    const count = AGREEMENT_PRODUCT_PAGE_SIZE;
    dispatch(
      fetchProducts(contractAddress, agreementAddress, {
        start,
        count,
      }),
    );
  };

/**
 * Returns a redux api middleware action that makes a request to create an Agreement.
 *
 * @param {object} values - New Agreement field values.
 * @param {string} contractAddress - Contract address.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const createAgreement = (contractAddress, values) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/agreements`,
    method: 'POST',
    body: JSON.stringify(values),
    types: [
      { type: CREATE_AGREEMENT_REQUEST },
      {
        type: CREATE_AGREEMENT_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
        meta: { contractAddress },
      },
      {
        type: CREATE_AGREEMENT_FAILURE,
        payload: handleError,
        meta: { contractAddress },
      },
    ],
  });

/**
 * Returns a redux api middleware action that makes a request to edit an Agreement.
 *
 * @param {object} values - Updated Agreement field values.
 * @param {string} contractAddress - Contract address.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const editAgreement = (contractAddress, agreementAddress, values) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/agreements/${agreementAddress}`,
    method: 'PUT',
    body: JSON.stringify(values),
    types: [
      { type: EDIT_AGREEMENT_REQUEST },
      {
        type: EDIT_AGREEMENT_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
        meta: { contractAddress },
      },
      {
        type: EDIT_AGREEMENT_FAILURE,
        payload: handleError,
        meta: { contractAddress },
      },
    ],
  });

/**
 * Submits an agreement for approval.
 *
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const submitForApproval = (contractAddress, agreementAddress) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/agreements/${agreementAddress}/submit-for-approval`,
    method: 'PATCH',
    types: [
      { type: SUBMIT_AGREEMENT_FOR_APPROVAL_REQUEST },
      {
        type: SUBMIT_AGREEMENT_FOR_APPROVAL_SUCCESS,
        payload: (action, state, res) => handleResponse(agreementSchema, action, state, res),
      },
      {
        type: SUBMIT_AGREEMENT_FOR_APPROVAL_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Redux api middleware action that makes a request to approve an Agreement.
 *
 * @param {string} contractAddress - Contract address.
 * @param {string} agreementAddress - Agreement address.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const approveAgreement = (contractAddress, agreementAddress) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/agreements/${agreementAddress}/approve`,
    method: 'PATCH',
    types: [
      { type: APPROVE_AGREEMENT_REQUEST },
      {
        type: APPROVE_AGREEMENT_SUCCESS,
        payload: (action, state, res) => handleResponse(agreementSchema, action, state, res),
      },
      {
        type: APPROVE_AGREEMENT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Redux api middleware action that makes a request to reject an Agreement.
 *
 * @param {string} contractAddress - Contract address.
 * @param {string} agreementAddress - Agreement address.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const rejectAgreement = (contractAddress, agreementAddress) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/agreements/${agreementAddress}/reject`,
    method: 'PATCH',
    types: [
      { type: REJECT_AGREEMENT_REQUEST },
      {
        type: REJECT_AGREEMENT_SUCCESS,
        payload: (action, state, res) => handleResponse(agreementSchema, action, state, res),
      },
      {
        type: REJECT_AGREEMENT_FAILURE,
        payload: handleError,
      },
    ],
  });
